/* Phone barcode scanner (Database A / Returned Parcels). zxing-cpp (WASM, try-harder) on full-res camera frames. */
import { useEffect, useRef, useState } from "react";
import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Camera } from "lucide-react";

prepareZXingModule({ overrides: { locateFile: (p, prefix) => (p.endsWith(".wasm") ? `/${p}` : prefix + p) } });
const OPTS = { formats: ["Code128", "Code39", "Code93", "ITF", "EAN13", "QRCode", "DataMatrix"], tryHarder: true,
  tryRotate: true, tryInvert: false, maxNumberOfSymbols: 1 };

const decode = async (src) => {
  try { const r = await readBarcodes(src, OPTS); return r.find((x) => x.isValid && x.text)?.text || ""; }
  catch { return ""; }
};

// onCode(text) → Promise<boolean>: true = found (close), false = keep scanning.
// continuous: camera stays open; onCode returns { ok, msg } shown as a status line. `top` = extra controls.
export const DbaScanDialog = ({ open, onClose, onCode, continuous = false, top = null, title = "Scan parcel barcode", resetKey = "" }) => {
  const [msg, setMsg] = useState("");
  const [okMsg, setOkMsg] = useState(false);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const last = useRef({ code: "", at: 0 });
  const videoRef = useRef(null);
  const lock = useRef(false);
  const codeRef = useRef(onCode);
  codeRef.current = onCode;
  useEffect(() => { last.current = { code: "", at: 0 }; }, [resetKey]);  // e.g. reason changed → same label may be retried

  const lookup = async (text) => {
    const code = String(text || "").replace(/\s+/g, "").toUpperCase();
    if (!code) return;
    if (continuous && last.current.code === code && Date.now() - last.current.at < 4000) return;  // same label still in view
    setBusy(true);
    const found = await codeRef.current(code);
    setBusy(false);
    if (continuous) {
      last.current = { code, at: Date.now() };
      setTyped(""); setOkMsg(!!found?.ok); setMsg(found?.msg || ""); return;
    }
    if (found) { setTyped(""); onClose(); } else { setOkMsg(false); setMsg(`Not found: ${code}`); }
  };

  // Still photo = full resolution + autofocus: most reliable for small / dense labels.
  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true); setMsg("");
    const text = await decode(f);
    setBusy(false);
    if (text) await lookup(text);
    else { setOkMsg(false); setMsg("No barcode found in the photo — hold the phone closer so the barcode fills the photo, avoid glare"); }
  };

  useEffect(() => {
    if (!open) return undefined;
    setMsg(""); lock.current = false;
    let alive = true; let stream = null; let timer = null;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = async () => {
      const v = videoRef.current;
      if (!alive) return;
      if (v && v.readyState >= 2 && !lock.current) {
        // Centre band at native resolution (where the on-screen guide is).
        const w = v.videoWidth, h = v.videoHeight, bh = Math.round(h * 0.6);
        canvas.width = w; canvas.height = bh;
        ctx.drawImage(v, 0, Math.round((h - bh) / 2), w, bh, 0, 0, w, bh);
        const text = await decode(ctx.getImageData(0, 0, w, bh));
        if (text && alive) {
          lock.current = true;
          if (navigator.vibrate) navigator.vibrate(80);
          await lookup(text);
          setTimeout(() => { lock.current = false; }, 1500);
        }
      }
      if (alive) timer = setTimeout(tick, 120);
    };
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: {
          facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } } });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        const track = stream.getVideoTracks()[0];
        try { await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* not supported */ }
        const v = videoRef.current;
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); }
        tick();
      } catch {
        if (alive) { setOkMsg(false); setMsg("Camera not available — allow camera access, or take a photo below"); }
      }
    })();
    return () => {
      alive = false; clearTimeout(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  // eslint-disable-next-line
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm p-4" data-testid="dba-scan-dialog">
        <DialogHeader><DialogTitle className="text-sm">{title}</DialogTitle></DialogHeader>
        {top}
        <div id="dba-scan-region" className="relative w-full overflow-hidden rounded-lg bg-slate-900 aspect-[4/3]">
          <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-x-[4%] top-[20%] bottom-[20%] rounded-md border-2 border-white/90 shadow-[0_0_0_999px_rgba(0,0,0,0.35)]" />
          <div className="pointer-events-none absolute inset-x-[6%] top-1/2 h-0.5 bg-red-500/80" />
        </div>
        <div className="min-h-[20px] text-xs flex items-center gap-1.5" data-testid="dba-scan-msg">
          {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading…</>
            : msg ? <span className={`font-semibold ${okMsg ? "text-emerald-700" : "text-red-600"}`}>{msg}</span>
            : <span className="text-slate-500">Bring the barcode close so it fills the box, lines across the red line</span>}
        </div>
        <label className="flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 py-2 text-xs font-semibold text-indigo-700 active:bg-indigo-100" data-testid="dba-scan-photo-btn">
          <Camera className="w-4 h-4" /> Not reading? Take a photo of the barcode
          <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="dba-scan-photo-input" onChange={onPhoto} />
        </label>
        <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); lookup(typed); }}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Or type tracking number"
            className="flex-1 min-w-0 rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono uppercase" data-testid="dba-scan-manual" />
          <button type="submit" disabled={busy} className="rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white disabled:opacity-60" data-testid="dba-scan-manual-go">Go</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
